import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trophy, ArrowUpDown, Target, BadgeCheck } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";

// Sortable numeric columns (Tot. tours staat apart, helemaal rechts)
const columns = [
  { key: "total_points", label: "Punten" },
  { key: "count_100plus", label: "100+" },
  { key: "count_140plus", label: "140+" },
  { key: "count_180", label: "180" },
  { key: "bavg", label: "TOT. AVG" },
  { key: "matches_total", label: "Tot. Games" },
  { key: "matches_won", label: "Games Won" },
  { key: "legs_total", label: "Tot. Legs" },
  { key: "legs_won", label: "Legs Won" },
];
const playedCol = { key: "tournaments_played", label: "Tot. tours" };
const sortOptions = [...columns, playedCol];

const rankBadge = {
  1: "bg-[#D4AF37] text-[#0A0A0A]",
  2: "bg-[#A0A0A0] text-white",
  3: "bg-[#CD7F32] text-white",
};

export default function Leaderboard() {
  const [board, setBoard] = useState([]);
  const [finishes, setFinishes] = useState([]);
  const [sortKey, setSortKey] = useState("total_points");

  useEffect(() => {
    api.get("/leaderboard").then(({ data }) => setBoard(data));
    api.get("/high-finishes").then(({ data }) => setFinishes(data));
  }, []);

  const sorted = [...board].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
  const colSpan = columns.length + 3; // rank, player, + numeric + best legs + gespeeld

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 lg:py-16">
      <div className="flex items-center gap-3 mb-2">
        <Trophy className="w-7 h-7 text-[#D4AF37]" />
        <h1 className="font-heading text-4xl lg:text-5xl tracking-tighter font-black">Stand Ranking</h1>
      </div>
      <p className="text-[#525252] mb-8">
        Spelers gerangschikt op totaal aantal punten over alle toernooien van het seizoen.
      </p>

      <div className="flex items-center gap-2 mb-6 text-sm text-[#525252]" data-testid="finale-legend">
        <BadgeCheck className="w-5 h-5 text-[#16A34A]" />
        <span>Geplaatst voor de <strong className="text-[#0A0A0A]">finaledag</strong> (deelname aan minimaal 6 toernooien)</span>
      </div>

      <Tabs defaultValue="ranking">
        <TabsList className="bg-white border border-[#E5E5E5] rounded-none p-0 h-auto mb-6">
          <TabsTrigger value="ranking" data-testid="tab-ranking" className="rounded-none px-5 py-3 data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-[#D4AF37]">
            Ranking
          </TabsTrigger>
          <TabsTrigger value="finishes" data-testid="tab-finishes" className="rounded-none px-5 py-3 data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-[#D4AF37]">
            Hoogste finishes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ranking">
          <div className="flex items-center gap-2 mb-4" data-testid="ranking-sort-control">
            <span className="text-xs uppercase tracking-wider font-semibold text-[#525252]">Sorteer op</span>
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              data-testid="ranking-sort-select"
              className="border border-[#E5E5E5] bg-white py-2 px-3 text-sm font-semibold focus:outline-none focus:border-[#D4AF37] cursor-pointer"
            >
              {sortOptions.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full overflow-x-auto bg-white border border-[#E5E5E5]"
          >            <table className="w-full" data-testid="leaderboard-table">
              <thead>
                <tr className="bg-[#FDFBF7] border-b border-[#E5E5E5]">
                  <th className="text-left text-[10px] uppercase tracking-wider font-semibold text-[#525252] py-3 px-2 w-12 align-bottom">
                    Plaats
                  </th>
                  <th className="text-left text-[10px] uppercase tracking-wider font-semibold text-[#525252] py-3 px-2 align-bottom">
                    Speler
                  </th>
                  {columns.map((c) => (
                    <th key={c.key} className="py-3 px-1.5 align-bottom">
                      <button
                        onClick={() => setSortKey(c.key)}
                        data-testid={`sort-${c.key}`}
                        className={`flex flex-col items-end gap-0.5 ml-auto text-[10px] uppercase tracking-wider font-semibold leading-tight transition-colors ${
                          sortKey === c.key ? "text-[#D4AF37]" : "text-[#525252] hover:text-[#0A0A0A]"
                        }`}
                      >
                        <span className="block max-w-[3.25rem] break-words text-right">{c.label}</span>
                        <ArrowUpDown className="w-2.5 h-2.5 shrink-0" />
                      </button>
                    </th>
                  ))}
                  <th className="py-3 px-1.5 text-right text-[10px] uppercase tracking-wider font-semibold text-[#525252] align-bottom">
                    <span className="block max-w-[3.25rem] break-words ml-auto leading-tight">Top 3 legs</span>
                  </th>
                  <th className="py-3 px-1.5 align-bottom">
                    <button
                      onClick={() => setSortKey(playedCol.key)}
                      data-testid={`sort-${playedCol.key}`}
                      className={`flex flex-col items-end gap-0.5 ml-auto text-[10px] uppercase tracking-wider font-semibold leading-tight transition-colors ${
                        sortKey === playedCol.key ? "text-[#D4AF37]" : "text-[#525252] hover:text-[#0A0A0A]"
                      }`}
                    >
                      <span className="block max-w-[3.25rem] break-words text-right">{playedCol.label}</span>
                      <ArrowUpDown className="w-2.5 h-2.5 shrink-0" />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} className="py-16 text-center text-[#525252]">
                      Nog geen resultaten ingevoerd.
                    </td>
                  </tr>
                ) : (
                  sorted.map((p, idx) => (
                    <tr
                      key={p.player_id}
                      data-testid={`leaderboard-row-${idx}`}
                      className="border-b border-[#E5E5E5] last:border-0 hover:bg-[#FDFBF7] transition-colors"
                    >
                      <td className="py-2.5 px-2">
                        <span
                          className={`inline-flex items-center justify-center w-7 h-7 font-mono font-bold text-xs ${
                            rankBadge[idx + 1] || "bg-[#F2F0EA] text-[#0A0A0A]"
                          }`}
                        >
                          {idx + 1}
                        </span>
                      </td>
                      <td className="py-2.5 px-2 font-semibold text-[#0A0A0A] text-sm whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5">
                          {p.qualified_for_finale && (
                            <BadgeCheck className="w-4 h-4 text-[#16A34A] shrink-0" data-testid={`finale-badge-${idx}`} title="Geplaatst voor de finaledag" />
                          )}
                          {p.name}
                        </span>
                      </td>
                      {columns.map((c) => (
                        <td
                          key={c.key}
                          className={`py-2.5 px-1.5 text-right font-mono text-xs ${
                            c.key === sortKey ? "font-bold text-[#0A0A0A]" : "text-[#525252]"
                          }`}
                        >
                          {p[c.key]}{c.suffix || ""}
                        </td>
                      ))}
                      <td className="py-2.5 px-1.5 text-right font-mono text-xs text-[#525252] whitespace-nowrap">
                        {p.best_legs && p.best_legs.length ? p.best_legs.join(", ") : "—"}
                      </td>
                      <td className={`py-2.5 px-1.5 text-right font-mono text-xs ${
                        sortKey === playedCol.key ? "font-bold text-[#0A0A0A]" : "text-[#525252]"
                      }`}>
                        {p.tournaments_played}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </motion.div>
        </TabsContent>

        <TabsContent value="finishes">
          <FinishesTable finishes={finishes} showTournament />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export const FinishesTable = ({ finishes, showTournament = false }) => {
  if (!finishes || finishes.length === 0) {
    return (
      <div className="bg-white border border-[#E5E5E5] p-12 text-center text-[#525252]">
        Nog geen hoogste finishes ingevoerd.
      </div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full overflow-x-auto bg-white border border-[#E5E5E5]"
      data-testid="finishes-table"
    >
      <table className="w-full">
        <thead>
          <tr className="bg-[#FDFBF7] border-b border-[#E5E5E5] text-xs uppercase tracking-[0.12em] text-[#525252]">
            <th className="text-left py-4 px-4 w-16">Plaats</th>
            <th className="text-left py-4 px-4">Finish</th>
            <th className="text-left py-4 px-4">Speler</th>
            {showTournament && <th className="text-left py-4 px-4">Toernooi</th>}
          </tr>
        </thead>
        <tbody>
          {finishes.map((f, idx) => (
            <tr key={f.id} data-testid={`finish-row-${idx}`} className="border-b border-[#E5E5E5] last:border-0 hover:bg-[#FDFBF7] transition-colors">
              <td className="py-3.5 px-4">
                <span className={`inline-flex items-center justify-center w-8 h-8 font-mono font-bold text-sm ${rankBadge[idx + 1] || "bg-[#F2F0EA] text-[#0A0A0A]"}`}>
                  {idx + 1}
                </span>
              </td>
              <td className="py-3.5 px-4">
                <span className="inline-flex items-center gap-2 font-mono text-xl font-black text-[#A8882D]">
                  <Target className="w-4 h-4 text-[#D4AF37]" /> {f.value}
                </span>
              </td>
              <td className="py-3.5 px-4 font-semibold text-[#0A0A0A] whitespace-nowrap">{f.player_name}</td>
              {showTournament && <td className="py-3.5 px-4 text-sm text-[#525252]">{f.tournament_name || "—"}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </motion.div>
  );
};
